/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, IconButton } from '@mui/material';
import { useCustomNavigate } from '../../../../../../shared/hooks/use-conversation-navigate';
import { QSTypography } from '../../../../../../shared/components/atoms';
import { chatListAtom } from '../../../states/chat-list';
import { teamMembersAtom } from '../../../states/team-members';
import {
  wabaIntegrationsAtomFamily,
  wabaIntegrationsOriginalAtomFamily,
  wabaIntegrationsSearchAtomFamily,
} from '../../../../../../shared/states/waba';
import { ALL_CHANNELS, DEFAULT_MEMBER, DEFAULT_TEAM } from '../../../../../../shared/constants';
import {
  paginatedFiltersConversationTagsAtom,
  paginatedFiltersConversationTagsOriginalAtom,
  paginatedFiltersConversationTagsSearchAtom,
} from '../../../states/conversation-tags';
import CloseIcon from '../../../../../../shared/icons/icons-revamp/close';
import DropdownVirtualListWithConfirm from '../../../../../../shared/modules/dropdown/DropdownVirtualListWithConfirm';
import { ChatListAtom, ConversationTag, GetConversationTagsPaginationData, TeamMember } from '../../../typings';
import useQuery from '../../../../../../shared/hooks/useQuery';
import usePaginatedQuery from '../../../../../../shared/hooks/usePaginatedQuery';
import { getTeamMembers } from '../../../requests/get-team-members';
import { getConversationTagsV2 } from '../../../requests/get-conversation-tags';
import { getWabaIntegrations } from '../../../../../../shared/requests/waba';
import {
  WabaChannelSort,
  WabaChannelStatus,
  WabaIntegration,
  WabaIntegrationPaginationParams,
} from '../../../../../../shared/typings/waba';
import useDebounce from '../../../../../../shared/hooks/use-debounce';
import { debounce } from '@mui/material';
import { useHasAnyFilters } from '../../../hooks/use-has-any-filters';
import WabaIntegrationIcon from '../../../../../../shared/components/molecules/waba-integration-logo';
import { CustomFieldFilterAppliedRow } from '../custom-field-filter';
import { filterCompleteConditions } from '../custom-field-filter/utils';

// Common styles for filter rows
const filterRowStyles = {
  container: css`
    display: inline-flex;
    align-items: center;
    margin: 0 16px;
    gap: 0;
    min-height: 28px;
    border: 1px solid rgba(30, 41, 59, 0.14);
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 6px;
  `,
  label: css`
    color: #7a7a7a;
    font-family: Inter;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 19.6px;
    background-color: #f6f6f6;
    padding: 4px 8px;
    border-right: 1px solid rgba(224, 224, 234, 0.5);
  `,
  dropdownContainer: css`
    flex: 1;
    position: relative;
    min-width: 0;
  `,
  closeButton: css`
    padding: 4px;
    color: #2d2d2d;
    margin-left: auto;
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    &:hover {
      background-color: rgba(0, 0, 0, 0.06);
    }
    &:active {
      background-color: rgba(0, 0, 0, 0.12);
    }
  `,
  dropdownContainerStyles: css`
    width: 100%;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    row-gap: 8px;
  `,
  dropdownPlaceholderStyles: css`
    font-size: 14px;
    color: #2d2d2d;
    font-weight: 400;
    margin-right: 0;
    max-width: 100%;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  `,
  dropdownButtonStyles: css`
    background-color: transparent;
    border: none;
    padding: 0;
    border-radius: 0;
    justify-content: flex-start;
    text-transform: none;
    min-height: auto;
    height: auto;
    height: 28px;
    font-size: 14px;
    width: 100%;
    max-width: 200px;
    padding: 0 8px;
    min-width: 0;
    overflow: hidden;
    &:hover {
      background-color: rgba(0, 0, 0, 0.06);
    }
    &:active {
      background-color: rgba(0, 0, 0, 0.12);
    }
  `,
  dropdownCaretIconCss: css`
    display: none;
  `,
  dropdownEmptyDataMessageStyles: css`
    font-size: 14px;
    font-weight: 400;
    word-wrap: break-word;
    word-break: break-all;
  `,
};

const AppliedFilters = () => {
  const location = useLocation();
  const routerNavigate = useNavigate();
  const navigate = useCustomNavigate();
  const state = useAtomValue(chatListAtom) as ChatListAtom;

  const { tags, assignedUserId, waba, tagIds, conversationView, customFieldConditions } = state;
  const completeCustomFieldConditions = useMemo(
    () => filterCompleteConditions(customFieldConditions ?? []),
    [customFieldConditions]
  );

  // Get waba from state, with fallback to URL params for consistency
  const queryParams = new URLSearchParams(location.search);
  const wabaFromParams = queryParams.has('waba') ? (queryParams.get('waba') as string).split(',').filter(Boolean) : [];

  // Ensure waba is always an array for consistent checking
  // Use state value if available, otherwise fall back to URL params
  const wabaArray = Array.isArray(waba) && waba.length > 0 ? waba : wabaFromParams;

  // Fetch team members
  const { requestData: conversationAssigneesRequestData } = useQuery<TeamMember[]>({
    requestAtom: teamMembersAtom,
    queryFunction: getTeamMembers,
    getCachedData: true,
  });

  // Fetch tags
  const [conversationTagsSearchTerm, setConversationTagsSearchTerm] = useState<string>('');
  const debouncedConversationTagsSearchTerm = useDebounce<string>(conversationTagsSearchTerm ?? '', 300);
  const lastFetchedTagsSearchTermRef = useRef<string>('');
  const refreshConversationTagsSearchRef = useRef<(() => Promise<void>) | null>(null);

  // Getter for initial tags list (without search)
  const conversationTagsInitialGetter = useCallback(
    async (paginationData?: GetConversationTagsPaginationData) => {
      const {
        tags: tagsData,
        hasMore,
        nextSearchAfter,
      } = await getConversationTagsV2({
        searchAfter: paginationData?.nextSearchAfter,
        searchTerm: '',
        selectedTags: tags,
      });

      return {
        data: tagsData,
        paginationData: {
          hasMore,
          nextSearchAfter,
        },
      };
    },
    [tags]
  );

  // Getter for tags search results
  const conversationTagsSearchGetter = useCallback(
    async (paginationData?: GetConversationTagsPaginationData) => {
      const {
        tags: tagsData,
        hasMore,
        nextSearchAfter,
      } = await getConversationTagsV2({
        searchAfter: paginationData?.nextSearchAfter,
        searchTerm: debouncedConversationTagsSearchTerm,
        selectedTags: [],
      });

      return {
        data: tagsData,
        paginationData: {
          hasMore,
          nextSearchAfter,
        },
      };
    },
    [debouncedConversationTagsSearchTerm]
  );

  // Query for initial tags list
  const { fetchMore: conversationTagsFetchMore, requestData: conversationTagsRequestData } = usePaginatedQuery<
    ConversationTag,
    GetConversationTagsPaginationData
  >({
    requestAtom: paginatedFiltersConversationTagsAtom,
    getter: conversationTagsInitialGetter,
    autoFetch: true,
  });

  // Query for tags search results
  const {
    fetchMore: conversationTagsSearchFetchMore,
    requestData: conversationTagsSearchRequestData,
    refresh: refreshConversationTagsSearch,
  } = usePaginatedQuery<ConversationTag, GetConversationTagsPaginationData>({
    requestAtom: paginatedFiltersConversationTagsSearchAtom,
    getter: conversationTagsSearchGetter,
    autoFetch: false,
  });

  // Store refresh function in ref to avoid dependency issues
  refreshConversationTagsSearchRef.current = refreshConversationTagsSearch;

  // State for dropdown visibility
  const [tagFilterDropdownOpen, setTagFilterDropdownOpen] = useState(false);
  const [assigneeFilterDropdownOpen, setAssigneeFilterDropdownOpen] = useState(false);
  const [wabaFilterDropdownOpen, setWabaFilterDropdownOpen] = useState(false);

  // Team member search - immediate (no debounce)
  const [conversationAssigneeSearchTerm, setConversationAssigneeSearchTerm] = useState<string>('');

  // Fetch WABA integrations - buffer pattern for instant UI
  const [wabaIntegrationSearchTermBuffer, setWabaIntegrationSearchTermBuffer] = useState<string>('');
  const [wabaIntegrationSearchTerm, setWabaIntegrationSearchTerm] = useState<string>('');
  // Debounced value only for API calls, not for client-side filtering
  const debouncedWabaIntegrationSearchTerm = useDebounce(wabaIntegrationSearchTerm ?? '', 500);
  const lastFetchedWabaSearchTermRef = useRef<string>('');
  const refreshWabaIntegrationsSearchRef = useRef<(() => Promise<void>) | null>(null);

  // Debounce buffer to actual searchTerm
  useEffect(() => {
    const timer = setTimeout(() => {
      setWabaIntegrationSearchTerm(wabaIntegrationSearchTermBuffer);
    }, 300); // 300ms debounce like tags

    return () => {
      clearTimeout(timer);
    };
  }, [wabaIntegrationSearchTermBuffer]);

  const wabaIntegrationsAtom = useMemo(() => {
    return wabaIntegrationsAtomFamily({ id: ALL_CHANNELS });
  }, []);

  const wabaIntegrationsSearchAtom = useMemo(() => {
    return wabaIntegrationsSearchAtomFamily({ id: ALL_CHANNELS });
  }, []);

  // Getter for initial WABA list (without search)
  const wabaIntegrationsInitialGetter = useCallback(async (paginationParams?: WabaIntegrationPaginationParams) => {
    if (!paginationParams) {
      paginationParams = {
        teamId: DEFAULT_TEAM,
        forMemberId: undefined,
        sendOnlyAssignedIntegrations: false,
        excludePrimaryField: true,
        includeAllIntegrations: true,
        sort: WabaChannelSort.NAME_ASC,
        sortAfter: undefined,
        searchTerm: '',
        hasMore: false,
      };
    }
    const data = await getWabaIntegrations(paginationParams, true);
    const { integrations, nextSortAfter, hasMore } = data || {};
    return {
      data: integrations,
      paginationData: {
        ...paginationParams,
        sortAfter: nextSortAfter ?? paginationParams?.nextSortAfter,
        searchTerm: '',
        hasMore: hasMore ?? paginationParams.hasMore,
      } as WabaIntegrationPaginationParams,
    };
  }, []);

  // Getter for WABA search results
  const wabaIntegrationsSearchGetter = useCallback(
    async (paginationParams?: WabaIntegrationPaginationParams) => {
      if (!paginationParams) {
        paginationParams = {
          teamId: DEFAULT_TEAM,
          forMemberId: undefined,
          sendOnlyAssignedIntegrations: false,
          excludePrimaryField: true,
          includeAllIntegrations: true,
          sort: WabaChannelSort.NAME_ASC,
          sortAfter: undefined,
          searchTerm:
            typeof debouncedWabaIntegrationSearchTerm === 'string' &&
            debouncedWabaIntegrationSearchTerm.trim().length > 0
              ? debouncedWabaIntegrationSearchTerm.trim()
              : '',
          hasMore: false,
        };
      }
      const data = await getWabaIntegrations(paginationParams, true);
      const { integrations, nextSortAfter, hasMore } = data || {};
      return {
        data: integrations,
        paginationData: {
          ...paginationParams,
          sortAfter: nextSortAfter ?? paginationParams?.nextSortAfter,
          searchTerm:
            typeof debouncedWabaIntegrationSearchTerm === 'string' &&
            debouncedWabaIntegrationSearchTerm.trim().length > 0
              ? debouncedWabaIntegrationSearchTerm.trim()
              : '',
          hasMore: hasMore ?? paginationParams.hasMore,
        } as WabaIntegrationPaginationParams,
      };
    },
    [debouncedWabaIntegrationSearchTerm]
  );

  // Query for initial WABA list (fetched when dropdown opens via onDropdownOpen)
  const {
    fetchMore: wabaIntegrationsFetchMore,
    requestData: wabaIntegrationsRequestData,
    refresh: refreshWabaIntegrations,
  } = usePaginatedQuery<WabaIntegration, WabaIntegrationPaginationParams>({
    requestAtom: wabaIntegrationsAtom,
    getter: wabaIntegrationsInitialGetter,
    autoFetch: false,
  });

  // Query for WABA search results
  const {
    fetchMore: wabaIntegrationsSearchFetchMore,
    requestData: wabaIntegrationsSearchRequestData,
    refresh: refreshWabaIntegrationsSearch,
  } = usePaginatedQuery<WabaIntegration, WabaIntegrationPaginationParams>({
    requestAtom: wabaIntegrationsSearchAtom,
    getter: wabaIntegrationsSearchGetter,
    autoFetch: false,
  });

  // Store refresh function in ref to avoid dependency issues
  refreshWabaIntegrationsSearchRef.current = refreshWabaIntegrationsSearch;

  // Get WABA display names - use both search results and original data
  const wabaIntegrationsData = useAtomValue(wabaIntegrationsAtom);
  const setWabaIntegrationsData = useSetAtom(wabaIntegrationsAtom);
  const wabaIntegrationsOriginalAtom = useMemo(() => {
    return wabaIntegrationsOriginalAtomFamily({ id: DEFAULT_MEMBER });
  }, []);
  const wabaIntegrationsOriginalData = useAtomValue(wabaIntegrationsOriginalAtom);
  const setWabaIntegrationsOriginalData = useSetAtom(wabaIntegrationsOriginalAtom);

  // Get tags original data for lookup
  const conversationTagsOriginalRequestData = useAtomValue(paginatedFiltersConversationTagsOriginalAtom);

  // Trigger tags search when search term changes
  useEffect(() => {
    const trimmedSearchTerm = debouncedConversationTagsSearchTerm?.trim() ?? '';
    const hasSearchTerm = trimmedSearchTerm.length > 0;
    const searchTermChanged = lastFetchedTagsSearchTermRef.current !== trimmedSearchTerm;
    const isNotLoading = !conversationTagsSearchRequestData?.loading && !conversationTagsSearchRequestData?.refreshing;

    if (hasSearchTerm && searchTermChanged && isNotLoading && refreshConversationTagsSearchRef.current) {
      lastFetchedTagsSearchTermRef.current = trimmedSearchTerm;
      refreshConversationTagsSearchRef.current();
    } else if (!hasSearchTerm) {
      // Reset ref when search term is cleared
      lastFetchedTagsSearchTermRef.current = '';
    }
  }, [
    debouncedConversationTagsSearchTerm,
    conversationTagsSearchRequestData?.loading,
    conversationTagsSearchRequestData?.refreshing,
  ]);

  // Trigger WABA search when search term changes
  useEffect(() => {
    const trimmedSearchTerm = debouncedWabaIntegrationSearchTerm?.trim() ?? '';
    const hasSearchTerm = trimmedSearchTerm.length > 0;
    const searchTermChanged = lastFetchedWabaSearchTermRef.current !== trimmedSearchTerm;
    const isNotLoading = !wabaIntegrationsSearchRequestData?.loading && !wabaIntegrationsSearchRequestData?.refreshing;

    if (hasSearchTerm && searchTermChanged && isNotLoading && refreshWabaIntegrationsSearchRef.current) {
      lastFetchedWabaSearchTermRef.current = trimmedSearchTerm;
      refreshWabaIntegrationsSearchRef.current();
    } else if (!hasSearchTerm) {
      // Reset ref when search term is cleared
      lastFetchedWabaSearchTermRef.current = '';
    }
  }, [
    debouncedWabaIntegrationSearchTerm,
    wabaIntegrationsSearchRequestData?.loading,
    wabaIntegrationsSearchRequestData?.refreshing,
  ]);

  // Merge WABA search results into original data so selected WABAs are available for display
  useEffect(() => {
    const searchResults = wabaIntegrationsSearchRequestData?.data;
    if (!searchResults || searchResults.length === 0) return;

    const originalData = wabaIntegrationsOriginalData?.data ?? [];
    const existingIds = new Set(originalData.map((waba) => waba.id));

    // Find WABAs in search results that aren't in original data
    const newWabas = searchResults.filter((waba) => !existingIds.has(waba.id));

    if (newWabas.length > 0) {
      // Merge new WABAs into original data
      setWabaIntegrationsOriginalData((prev) => ({
        ...prev,
        data: [...(prev.data ?? []), ...newWabas],
      }));
    }
  }, [wabaIntegrationsSearchRequestData?.data, wabaIntegrationsOriginalData?.data, setWabaIntegrationsOriginalData]);

  // Merge WABA search results into main data so selected WABAs are available for display
  useEffect(() => {
    const searchResults = wabaIntegrationsSearchRequestData?.data;
    if (!searchResults || searchResults.length === 0) return;

    const mainData = wabaIntegrationsData?.data ?? [];
    const existingIds = new Set(mainData.map((waba) => waba.id));

    // Find WABAs in search results that aren't in main data
    const newWabas = searchResults.filter((waba) => !existingIds.has(waba.id));

    if (newWabas.length > 0) {
      // Merge new WABAs into main data
      setWabaIntegrationsData((prev) => ({
        ...prev,
        data: [...(prev.data ?? []), ...newWabas],
      }));
    }
  }, [wabaIntegrationsSearchRequestData?.data, wabaIntegrationsData?.data, setWabaIntegrationsData]);

  // Determine which tags data to use: search results if searching, otherwise initial list
  const conversationTagsRequestDataForDropdown = useMemo(() => {
    const isSearching = conversationTagsSearchTerm && conversationTagsSearchTerm.trim().length > 0;
    return isSearching ? conversationTagsSearchRequestData : conversationTagsRequestData;
  }, [conversationTagsRequestData, conversationTagsSearchRequestData, conversationTagsSearchTerm]);

  // Determine which WABA data to use: search results if searching, otherwise initial list
  const wabaIntegrationsRequestDataForDropdown = useMemo(() => {
    const isSearching = wabaIntegrationSearchTerm && wabaIntegrationSearchTerm.trim().length > 0;
    return isSearching ? wabaIntegrationsSearchRequestData : wabaIntegrationsRequestData;
  }, [wabaIntegrationsRequestData, wabaIntegrationsSearchRequestData, wabaIntegrationSearchTerm]);

  // (Deprecated buffer-based assignee search removed in favor of immediate search)

  // Format filter values display
  const formatFilterValues = useCallback((values: string[]) => {
    if (values.length === 0) return '';
    if (values.length === 1) return values[0];
    return `${values.length} selected`;
  }, []);

  // Get tag names
  const tagNames = useMemo(() => {
    return tags ?? [];
  }, [tags]);

  // Get team member name
  const teamMemberName = useMemo(() => {
    if (!assignedUserId || assignedUserId === 'all' || assignedUserId === 'null') return null;
    const member = conversationAssigneesRequestData?.data?.find((member: TeamMember) => member.id === assignedUserId);
    return member?.name ?? null;
  }, [assignedUserId, conversationAssigneesRequestData]);

  // Get WABA names
  const wabaNames = useMemo(() => {
    if (!wabaArray || wabaArray.length === 0) return [];
    return wabaArray.map((wabaId: string) => {
      // First try to find in search results, then fallback to original data
      const waba =
        (wabaIntegrationsData?.data ?? [])?.find((waba: WabaIntegration) => waba.id === wabaId) ||
        (wabaIntegrationsOriginalData?.data ?? [])?.find((waba: WabaIntegration) => waba.id === wabaId);
      if (waba) {
        return waba.displayName ?? waba.name ?? waba.phoneNumber ?? wabaId;
      }
      return wabaId;
    });
  }, [wabaArray, wabaIntegrationsData, wabaIntegrationsOriginalData]);

  // Clear tags filter
  const handleClearTags = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation();
      }
      setTagFilterDropdownOpen(false);
      const queryParams = new URLSearchParams(window.location.search);
      queryParams.set('tags', '');
      queryParams.set('tagIds', '');
      navigate({ search: queryParams.toString() }, { replace: true });
    },
    [navigate]
  );

  // Clear assignee filter
  const handleClearAssignee = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation();
      }
      setAssigneeFilterDropdownOpen(false);
      const queryParams = new URLSearchParams(window.location.search);
      queryParams.set('assignedUserId', '');
      navigate({ search: queryParams.toString() }, { replace: true });
    },
    [navigate]
  );

  // Clear WABA filter
  const handleClearWaba = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation();
      }
      setWabaFilterDropdownOpen(false);
      const queryParams = new URLSearchParams(window.location.search);
      queryParams.set('waba', '');
      navigate({ search: queryParams.toString() }, { replace: true });
    },
    [navigate]
  );

  // Clear view filter (reset to default chats)
  const handleClearViewFilter = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation();
      }
      const queryParams = new URLSearchParams(location.search);
      queryParams.delete('chatType');
      const newSearch = queryParams.toString();
      routerNavigate(
        {
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : '',
        },
        { replace: true }
      );
    },
    [location.pathname, location.search, routerNavigate]
  );

  // Handle tag selection change
  const handleTagsChange = useCallback(
    (tagIds: string[]) => {
      const queryParams = new URLSearchParams(location.search);

      // Use both search results and original data to map IDs to names
      // This ensures we can find all selected tags even if they're not in search results
      const allTagsData = [
        ...(conversationTagsRequestDataForDropdown?.data ?? []),
        ...(conversationTagsOriginalRequestData?.data ?? []),
      ];
      // Remove duplicates by ID
      const uniqueTagsMap = new Map<string, ConversationTag>();
      allTagsData.forEach((tag) => {
        if (!uniqueTagsMap.has(tag.id)) {
          uniqueTagsMap.set(tag.id, tag);
        }
      });

      // Map tagIds to tag names
      // First try to find in the tags data sources
      const tagNamesFromData = new Map<string, string>();
      tagIds.forEach((tagId) => {
        const tag = uniqueTagsMap.get(tagId);
        if (tag) {
          tagNamesFromData.set(tagId, tag.name);
        }
      });

      // Use existing tags from state as fallback for tags not found in data sources
      const existingTagNames = tags ?? [];

      // Build final tag names array by mapping each tagId to its name
      // Priority: 1) data sources, 2) existing tags from state (preserve order)
      const finalTagNames: string[] = [];
      tagIds.forEach((tagId, index) => {
        // First try to get from data sources
        const nameFromData = tagNamesFromData.get(tagId);
        if (nameFromData) {
          finalTagNames.push(nameFromData);
        } else if (index < existingTagNames.length) {
          // Fallback to existing tag name from state (preserve order)
          finalTagNames.push(existingTagNames[index]);
        } else {
          // Last resort: use tagId as name (shouldn't happen, but prevents errors)
          finalTagNames.push(tagId);
        }
      });

      const serializedTags = JSON.stringify(finalTagNames);
      const serializedTagIds = JSON.stringify(tagIds);
      queryParams.set('tags', serializedTags);
      queryParams.set('tagIds', serializedTagIds);
      navigate({ search: queryParams.toString() }, { replace: true });
    },
    [location.search, navigate, conversationTagsRequestDataForDropdown, conversationTagsOriginalRequestData, tags]
  );

  // Handle assignee change
  const handleAssigneeChange = useCallback(
    (assignedUserId: string) => {
      const queryParams = new URLSearchParams(location.search);
      queryParams.set('assignedUserId', assignedUserId || '');
      navigate({ search: queryParams.toString() }, { replace: true });
    },
    [location.search, navigate]
  );

  // Handle WABA change
  const handleWabaChange = useCallback(
    (wabaIds: string[]) => {
      const queryParams = new URLSearchParams(location.search);
      queryParams.set('waba', wabaIds.join(','));
      navigate({ search: queryParams.toString() }, { replace: true });
    },
    [location.search, navigate]
  );

  // Check if any filters are applied
  const hasAnyFilters = useHasAnyFilters();

  if (!hasAnyFilters) {
    return null;
  }

  return (
    <Box
      css={css`
        width: 100%;
        background-color: #ffffff;
        border-bottom: 1px solid rgba(224, 224, 234, 1);
        padding-top: 8px;
      `}
    >
      {/* Tags Filter Row */}
      {tagNames.length > 0 && (
        <Box css={filterRowStyles.container}>
          <QSTypography css={filterRowStyles.label}>Tags</QSTypography>
          <Box css={filterRowStyles.dropdownContainer}>
            <DropdownVirtualListWithConfirm<ConversationTag, GetConversationTagsPaginationData, string, true>
              disabled={
                conversationTagsRequestDataForDropdown?.loading ||
                conversationTagsRequestDataForDropdown?.refreshing ||
                (typeof conversationTagsRequestDataForDropdown?.error?.message === 'string' &&
                  conversationTagsRequestDataForDropdown?.error?.message?.length > 0)
              }
              popoverValue={formatFilterValues(tagNames) || 'All'}
              searchTerm={conversationTagsSearchTerm}
              setSearchTerm={setConversationTagsSearchTerm}
              placeholder="Search tags"
              optionsEmptyMessage="No tags found"
              name={['name']}
              value={tagIds ?? []}
              onChange={handleTagsChange}
              closeOnSelect={false}
              multiple={true}
              hoverBorderColor="rgba(122, 122, 122, 0.50)"
              requestData={conversationTagsRequestDataForDropdown}
              onDropdownClose={() => {
                setTagFilterDropdownOpen(false);
                setConversationTagsSearchTerm('');
              }}
              onDropdownOpen={() => setTagFilterDropdownOpen(true)}
              fetchMore={
                conversationTagsSearchTerm && conversationTagsSearchTerm.trim().length > 0
                  ? conversationTagsSearchFetchMore
                  : conversationTagsFetchMore
              }
              open={tagFilterDropdownOpen}
              disablePortal={false}
              containerStyles={filterRowStyles.dropdownContainerStyles}
              placeholderStyles={filterRowStyles.dropdownPlaceholderStyles}
              buttonStyles={filterRowStyles.dropdownButtonStyles}
              caretIconCss={filterRowStyles.dropdownCaretIconCss}
              emptyDataMessageStyles={filterRowStyles.dropdownEmptyDataMessageStyles}
              paperWidth="300px"
              showCancelConfirmButtons={false}
              moveSelectedToTop={true}
            />
          </Box>
          <IconButton
            disableRipple
            onClick={(e) => {
              e.stopPropagation();
              handleClearTags(e);
            }}
            css={filterRowStyles.closeButton}
          >
            <CloseIcon size={16} color="#7A7A7A" />
          </IconButton>
        </Box>
      )}

      {/* Team Member Filter Row */}
      {teamMemberName && (
        <Box css={filterRowStyles.container}>
          <QSTypography css={filterRowStyles.label}>Team member</QSTypography>
          <Box css={filterRowStyles.dropdownContainer}>
            <DropdownVirtualListWithConfirm<TeamMember, { hasMore: boolean }, string, false>
              disabled={
                conversationAssigneesRequestData?.loading ||
                conversationAssigneesRequestData?.refreshing ||
                (typeof conversationAssigneesRequestData?.error?.message === 'string' &&
                  conversationAssigneesRequestData?.error?.message?.length > 0)
              }
              popoverValue={teamMemberName || 'All'}
              searchTerm={conversationAssigneeSearchTerm}
              setSearchTerm={setConversationAssigneeSearchTerm}
              placeholder="Search team member"
              optionsEmptyMessage="No team member found"
              name={['name']}
              value={assignedUserId || ''}
              onChange={handleAssigneeChange}
              closeOnSelect={false}
              multiple={false}
              hoverBorderColor="rgba(122, 122, 122, 0.50)"
              requestData={conversationAssigneesRequestData}
              open={assigneeFilterDropdownOpen}
              disablePortal={false}
              containerStyles={filterRowStyles.dropdownContainerStyles}
              placeholderStyles={filterRowStyles.dropdownPlaceholderStyles}
              buttonStyles={filterRowStyles.dropdownButtonStyles}
              caretIconCss={filterRowStyles.dropdownCaretIconCss}
              emptyDataMessageStyles={filterRowStyles.dropdownEmptyDataMessageStyles}
              onDropdownClose={() => {
                setAssigneeFilterDropdownOpen(false);
                setConversationAssigneeSearchTerm('');
              }}
              onDropdownOpen={() => setAssigneeFilterDropdownOpen(true)}
              paperWidth="300px"
              showCancelConfirmButtons={false}
              moveSelectedToTop={true}
            />
          </Box>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleClearAssignee(e);
            }}
            css={filterRowStyles.closeButton}
          >
            <CloseIcon size={16} color="#7A7A7A" />
          </IconButton>
        </Box>
      )}

      {/* Channels/WABA Filter Row */}
      {wabaArray.length > 0 && (
        <Box css={filterRowStyles.container}>
          <QSTypography css={filterRowStyles.label}>Channels</QSTypography>
          <Box css={filterRowStyles.dropdownContainer}>
            <DropdownVirtualListWithConfirm<WabaIntegration, WabaIntegrationPaginationParams, string, true>
              disabled={
                wabaIntegrationsRequestDataForDropdown?.loading ||
                wabaIntegrationsRequestDataForDropdown?.refreshing ||
                (typeof wabaIntegrationsRequestDataForDropdown?.error?.message === 'string' &&
                  wabaIntegrationsRequestDataForDropdown?.error?.message?.length > 0)
              }
              popoverValue={formatFilterValues(wabaNames) || 'All'}
              searchTerm={wabaIntegrationSearchTerm}
              setSearchTerm={setWabaIntegrationSearchTermBuffer}
              searchInputValue={wabaIntegrationSearchTermBuffer}
              placeholder="Search WABA"
              optionsEmptyMessage="No WABA found"
              name={['showWabaName', 'displayName', 'name', 'phoneNumber', 'id']}
              value={wabaArray}
              onChange={handleWabaChange}
              closeOnSelect={false}
              multiple={true}
              hoverBorderColor="rgba(122, 122, 122, 0.50)"
              fetchMore={
                wabaIntegrationSearchTerm && wabaIntegrationSearchTerm.trim().length > 0
                  ? wabaIntegrationsSearchFetchMore
                  : wabaIntegrationsFetchMore
              }
              requestData={wabaIntegrationsRequestDataForDropdown}
              open={wabaFilterDropdownOpen}
              disablePortal={false}
              containerStyles={filterRowStyles.dropdownContainerStyles}
              placeholderStyles={filterRowStyles.dropdownPlaceholderStyles}
              buttonStyles={filterRowStyles.dropdownButtonStyles}
              caretIconCss={filterRowStyles.dropdownCaretIconCss}
              emptyDataMessageStyles={filterRowStyles.dropdownEmptyDataMessageStyles}
              onDropdownClose={() => {
                setWabaFilterDropdownOpen(false);
                setWabaIntegrationSearchTermBuffer('');
                setWabaIntegrationSearchTerm('');
              }}
              onDropdownOpen={() => {
                setWabaFilterDropdownOpen(true);
                refreshWabaIntegrations();
              }}
              paperWidth="300px"
              showCancelConfirmButtons={false}
              moveSelectedToTop={true}
              renderItemPrefix={(waba) => (
                <WabaIntegrationIcon
                  size={28}
                  wabaIntegrationLogo={waba.logo ?? ''}
                  wabaIntegrationName={waba.displayName ?? waba.name ?? waba.phoneNumber ?? ''}
                  channelType={waba.type}
                />
              )}
            />
          </Box>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleClearWaba(e);
            }}
            css={filterRowStyles.closeButton}
          >
            <CloseIcon size={16} color="#7A7A7A" />
          </IconButton>
        </Box>
      )}

      {/* View Filter Row */}
      {conversationView === 'groups' && (
        <Box css={filterRowStyles.container}>
          <QSTypography css={filterRowStyles.label}>View</QSTypography>
          <Box
            css={css`
              font-size: 14px;
              color: #2d2d2d;
              font-weight: 400;
              margin-right: 0;
              max-width: 200px;
              text-overflow: ellipsis;
              white-space: nowrap;
              overflow: hidden;
              height: 28px;
              padding: 0 8px;
              display: flex;
              align-items: center;
            `}
          >
            Groups
          </Box>
          <IconButton
            disableRipple
            onClick={(e) => {
              e.stopPropagation();
              handleClearViewFilter(e);
            }}
            css={filterRowStyles.closeButton}
          >
            <CloseIcon size={16} color="#7A7A7A" />
          </IconButton>
        </Box>
      )}

      {conversationView !== 'groups' &&
        completeCustomFieldConditions.map((condition, index) => (
          <CustomFieldFilterAppliedRow
            key={`${condition.id}-${condition.operator}-${index}`}
            condition={condition}
            index={index}
          />
        ))}
    </Box>
  );
};

export default AppliedFilters;
